import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  BarChart3, 
  Clock, 
  AlertTriangle, 
  CheckCircle2, 
  TrendingUp,
  TrendingDown,
  User,
  Calendar,
  Download,
  RefreshCw,
  ClipboardList,
  Timer
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const JobCardReportsPage = ({ user, onLogout }) => {
  const [loading, setLoading] = useState(true);
  const [jobCards, setJobCards] = useState([]);
  const [therapists, setTherapists] = useState([]);
  const [services, setServices] = useState([]);
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);
  const [filterService, setFilterService] = useState('all');
  const [filterTherapist, setFilterTherapist] = useState('all');

  // Analytics data
  const [analytics, setAnalytics] = useState({
    totalJobCards: 0,
    completedJobCards: 0,
    avgCompletionTime: 0,
    stepAnalytics: [],
    therapistPerformance: [],
    timeBreaches: [],
    serviceStats: []
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      const [therapistsRes, servicesRes] = await Promise.all([
        api.get('/therapists'),
        api.get('/services')
      ]);
      
      setTherapists(therapistsRes.data);
      setServices(servicesRes.data.filter(s => s.type === 'time_based'));

      // Fetch all job cards in date range (we need to filter client-side for date range)
      const jobCardsRes = await api.get('/job-cards');
      
      // Filter by date range
      let filteredCards = jobCardsRes.data.filter(jc => {
        const cardDate = jc.scheduled_date;
        return cardDate >= dateFrom && cardDate <= dateTo;
      });

      // Apply service filter
      if (filterService !== 'all') {
        filteredCards = filteredCards.filter(jc => jc.service_id === filterService);
      }

      // Apply therapist filter
      if (filterTherapist !== 'all') {
        filteredCards = filteredCards.filter(jc => 
          jc.assigned_therapist_ids?.includes(filterTherapist)
        );
      }

      setJobCards(filteredCards);
      calculateAnalytics(filteredCards, servicesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to fetch report data');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, filterService, filterTherapist]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const calculateAnalytics = (cards, allServices) => {
    const completedCards = cards.filter(jc => jc.status === 'completed');
    
    // Calculate average completion time
    let totalCompletionTime = 0;
    let completionCount = 0;
    completedCards.forEach(jc => {
      if (jc.started_at && jc.completed_at) {
        const start = new Date(jc.started_at);
        const end = new Date(jc.completed_at);
        totalCompletionTime += (end - start) / (1000 * 60); // minutes
        completionCount++;
      }
    });

    // Step-level analytics
    const stepStats = {};
    cards.forEach(jc => {
      jc.procedure_steps?.forEach(step => {
        if (!stepStats[step.description]) {
          stepStats[step.description] = {
            description: step.description,
            totalTime: 0,
            count: 0,
            times: []
          };
        }
        if (step.time_taken_minutes !== undefined && step.time_taken_minutes !== null) {
          stepStats[step.description].totalTime += step.time_taken_minutes;
          stepStats[step.description].count++;
          stepStats[step.description].times.push(step.time_taken_minutes);
        }
      });
    });

    const stepAnalytics = Object.values(stepStats).map(stat => ({
      ...stat,
      avgTime: stat.count > 0 ? stat.totalTime / stat.count : 0,
      minTime: stat.times.length > 0 ? Math.min(...stat.times) : 0,
      maxTime: stat.times.length > 0 ? Math.max(...stat.times) : 0
    }));

    // Therapist performance
    const therapistStats = {};
    cards.forEach(jc => {
      jc.assigned_therapist_ids?.forEach((therapistId, idx) => {
        const therapistName = jc.assigned_therapist_names?.[idx] || 'Unknown';
        if (!therapistStats[therapistId]) {
          therapistStats[therapistId] = {
            id: therapistId,
            name: therapistName,
            totalCards: 0,
            completedCards: 0,
            totalTime: 0,
            completedWithTime: 0
          };
        }
        therapistStats[therapistId].totalCards++;
        if (jc.status === 'completed') {
          therapistStats[therapistId].completedCards++;
          if (jc.started_at && jc.completed_at) {
            const start = new Date(jc.started_at);
            const end = new Date(jc.completed_at);
            therapistStats[therapistId].totalTime += (end - start) / (1000 * 60);
            therapistStats[therapistId].completedWithTime++;
          }
        }
      });
    });

    const therapistPerformance = Object.values(therapistStats).map(stat => ({
      ...stat,
      avgTime: stat.completedWithTime > 0 ? stat.totalTime / stat.completedWithTime : 0,
      completionRate: stat.totalCards > 0 ? (stat.completedCards / stat.totalCards) * 100 : 0
    }));

    // Time breaches (steps taking >25% longer than average)
    const timeBreaches = [];
    cards.forEach(jc => {
      jc.procedure_steps?.forEach(step => {
        const stepStat = stepStats[step.description];
        if (stepStat && step.time_taken_minutes !== undefined && step.time_taken_minutes !== null) {
          const avgTime = stepStat.count > 0 ? stepStat.totalTime / stepStat.count : 0;
          const threshold = avgTime * 1.25; // 25% above average
          if (avgTime > 0 && step.time_taken_minutes > threshold) {
            timeBreaches.push({
              jobCardId: jc.id,
              patientName: jc.patient_name,
              serviceName: jc.service_name,
              stepDescription: step.description,
              timeTaken: step.time_taken_minutes,
              avgTime: avgTime,
              breachPercent: ((step.time_taken_minutes - avgTime) / avgTime) * 100,
              therapistName: step.completed_by_therapist_name,
              date: jc.scheduled_date
            });
          }
        }
      });
    });

    // Service stats
    const serviceStatsMap = {};
    cards.forEach(jc => {
      if (!serviceStatsMap[jc.service_id]) {
        serviceStatsMap[jc.service_id] = {
          serviceId: jc.service_id,
          serviceName: jc.service_name,
          totalCards: 0,
          completedCards: 0,
          totalTime: 0,
          completedWithTime: 0
        };
      }
      serviceStatsMap[jc.service_id].totalCards++;
      if (jc.status === 'completed') {
        serviceStatsMap[jc.service_id].completedCards++;
        if (jc.started_at && jc.completed_at) {
          const start = new Date(jc.started_at);
          const end = new Date(jc.completed_at);
          serviceStatsMap[jc.service_id].totalTime += (end - start) / (1000 * 60);
          serviceStatsMap[jc.service_id].completedWithTime++;
        }
      }
    });

    const serviceStats = Object.values(serviceStatsMap).map(stat => ({
      ...stat,
      avgTime: stat.completedWithTime > 0 ? stat.totalTime / stat.completedWithTime : 0
    }));

    setAnalytics({
      totalJobCards: cards.length,
      completedJobCards: completedCards.length,
      avgCompletionTime: completionCount > 0 ? totalCompletionTime / completionCount : 0,
      stepAnalytics: stepAnalytics.sort((a, b) => b.count - a.count),
      therapistPerformance: therapistPerformance.sort((a, b) => b.completedCards - a.completedCards),
      timeBreaches: timeBreaches.sort((a, b) => b.breachPercent - a.breachPercent),
      serviceStats: serviceStats.sort((a, b) => b.totalCards - a.totalCards)
    });
  };

  const exportReport = () => {
    const reportData = {
      dateRange: { from: dateFrom, to: dateTo },
      summary: {
        totalJobCards: analytics.totalJobCards,
        completedJobCards: analytics.completedJobCards,
        avgCompletionTime: analytics.avgCompletionTime.toFixed(1),
        completionRate: analytics.totalJobCards > 0 
          ? ((analytics.completedJobCards / analytics.totalJobCards) * 100).toFixed(1) 
          : 0
      },
      stepAnalytics: analytics.stepAnalytics,
      therapistPerformance: analytics.therapistPerformance,
      timeBreaches: analytics.timeBreaches,
      serviceStats: analytics.serviceStats
    };

    const blob = new Blob([JSON.stringify(reportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `job-card-report-${dateFrom}-to-${dateTo}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Report exported');
  };

  if (user?.role !== 'Admin') {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="p-6 text-center">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900">Access Restricted</h2>
          <p className="text-gray-500">Only admins can view reports.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <BarChart3 className="w-7 h-7 text-indigo-600" />
              Job Card Reports
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Analytics and performance metrics for job cards
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={fetchData} variant="outline" size="sm">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button onClick={exportReport} size="sm" className="bg-indigo-600 hover:bg-indigo-700">
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="w-40">
              <Label className="text-xs text-gray-500 mb-1 block">From Date</Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </div>
            <div className="w-40">
              <Label className="text-xs text-gray-500 mb-1 block">To Date</Label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>
            <div className="w-48">
              <Label className="text-xs text-gray-500 mb-1 block">Service</Label>
              <Select value={filterService} onValueChange={setFilterService}>
                <SelectTrigger>
                  <SelectValue placeholder="All Services" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Services</SelectItem>
                  {services.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-48">
              <Label className="text-xs text-gray-500 mb-1 block">Therapist</Label>
              <Select value={filterTherapist} onValueChange={setFilterTherapist}>
                <SelectTrigger>
                  <User className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="All Therapists" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Therapists</SelectItem>
                  {therapists.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading report data...</div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <ClipboardList className="w-8 h-8 text-indigo-600" />
                  <span className="text-3xl font-bold text-gray-900">{analytics.totalJobCards}</span>
                </div>
                <div className="text-sm text-gray-500">Total Job Cards</div>
              </div>
              <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <CheckCircle2 className="w-8 h-8 text-green-600" />
                  <span className="text-3xl font-bold text-green-700">{analytics.completedJobCards}</span>
                </div>
                <div className="text-sm text-gray-500">Completed</div>
                <div className="text-xs text-green-600 mt-1">
                  {analytics.totalJobCards > 0 
                    ? ((analytics.completedJobCards / analytics.totalJobCards) * 100).toFixed(0) 
                    : 0}% completion rate
                </div>
              </div>
              <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <Timer className="w-8 h-8 text-blue-600" />
                  <span className="text-3xl font-bold text-blue-700">{analytics.avgCompletionTime.toFixed(1)}</span>
                </div>
                <div className="text-sm text-gray-500">Avg Time (min)</div>
              </div>
              <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <AlertTriangle className="w-8 h-8 text-amber-600" />
                  <span className="text-3xl font-bold text-amber-700">{analytics.timeBreaches.length}</span>
                </div>
                <div className="text-sm text-gray-500">Time Breaches</div>
                <div className="text-xs text-amber-600 mt-1">&gt;25% above average</div>
              </div>
            </div>

            {/* Step Analytics */}
            <div className="grid grid-cols-2 gap-6 mb-6">
              <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-indigo-600" />
                  Step Time Analytics
                </h3>
                {analytics.stepAnalytics.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-4">No step data available</p>
                ) : (
                  <div className="space-y-3">
                    {analytics.stepAnalytics.slice(0, 10).map((step, idx) => (
                      <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-gray-700 text-sm">{step.description}</span>
                          <Badge className="bg-indigo-100 text-indigo-700">
                            {step.count} completions
                          </Badge>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-xs">
                          <div className="text-center p-2 bg-white rounded">
                            <div className="text-gray-500">Avg</div>
                            <div className="font-bold text-indigo-600">{step.avgTime.toFixed(1)} min</div>
                          </div>
                          <div className="text-center p-2 bg-white rounded">
                            <div className="text-gray-500">Min</div>
                            <div className="font-bold text-green-600">{step.minTime.toFixed(1)} min</div>
                          </div>
                          <div className="text-center p-2 bg-white rounded">
                            <div className="text-gray-500">Max</div>
                            <div className="font-bold text-amber-600">{step.maxTime.toFixed(1)} min</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Therapist Performance */}
              <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <User className="w-5 h-5 text-purple-600" />
                  Therapist Performance
                </h3>
                {analytics.therapistPerformance.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-4">No therapist data available</p>
                ) : (
                  <div className="space-y-3">
                    {analytics.therapistPerformance.map((therapist, idx) => (
                      <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-gray-700">{therapist.name}</span>
                          <div className="flex items-center gap-2">
                            {therapist.completionRate >= 80 ? (
                              <TrendingUp className="w-4 h-4 text-green-500" />
                            ) : therapist.completionRate >= 50 ? (
                              <TrendingUp className="w-4 h-4 text-amber-500" />
                            ) : (
                              <TrendingDown className="w-4 h-4 text-red-500" />
                            )}
                            <span className={`font-bold ${
                              therapist.completionRate >= 80 ? 'text-green-600' :
                              therapist.completionRate >= 50 ? 'text-amber-600' : 'text-red-600'
                            }`}>
                              {therapist.completionRate.toFixed(0)}%
                            </span>
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-xs">
                          <div className="text-center p-2 bg-white rounded">
                            <div className="text-gray-500">Total</div>
                            <div className="font-bold text-gray-700">{therapist.totalCards}</div>
                          </div>
                          <div className="text-center p-2 bg-white rounded">
                            <div className="text-gray-500">Completed</div>
                            <div className="font-bold text-green-600">{therapist.completedCards}</div>
                          </div>
                          <div className="text-center p-2 bg-white rounded">
                            <div className="text-gray-500">Avg Time</div>
                            <div className="font-bold text-blue-600">{therapist.avgTime.toFixed(1)} min</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Time Breaches */}
            <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm mb-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                Time Breaches (Steps &gt;25% Above Average)
              </h3>
              {analytics.timeBreaches.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4">No time breaches detected 🎉</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-2 px-3 text-gray-500 font-medium">Date</th>
                        <th className="text-left py-2 px-3 text-gray-500 font-medium">Patient</th>
                        <th className="text-left py-2 px-3 text-gray-500 font-medium">Service</th>
                        <th className="text-left py-2 px-3 text-gray-500 font-medium">Step</th>
                        <th className="text-left py-2 px-3 text-gray-500 font-medium">Therapist</th>
                        <th className="text-right py-2 px-3 text-gray-500 font-medium">Time</th>
                        <th className="text-right py-2 px-3 text-gray-500 font-medium">Avg</th>
                        <th className="text-right py-2 px-3 text-gray-500 font-medium">Breach</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analytics.timeBreaches.slice(0, 20).map((breach, idx) => (
                        <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-2 px-3">{breach.date}</td>
                          <td className="py-2 px-3 font-medium">{breach.patientName}</td>
                          <td className="py-2 px-3">{breach.serviceName}</td>
                          <td className="py-2 px-3">{breach.stepDescription}</td>
                          <td className="py-2 px-3">{breach.therapistName || '-'}</td>
                          <td className="py-2 px-3 text-right font-medium text-amber-600">
                            {breach.timeTaken.toFixed(1)} min
                          </td>
                          <td className="py-2 px-3 text-right text-gray-500">
                            {breach.avgTime.toFixed(1)} min
                          </td>
                          <td className="py-2 px-3 text-right">
                            <Badge className="bg-red-100 text-red-700">
                              +{breach.breachPercent.toFixed(0)}%
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Service Stats */}
            <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-green-600" />
                Service Statistics
              </h3>
              {analytics.serviceStats.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4">No service data available</p>
              ) : (
                <div className="grid grid-cols-3 gap-4">
                  {analytics.serviceStats.map((service, idx) => (
                    <div key={idx} className="p-4 bg-gray-50 rounded-lg">
                      <h4 className="font-medium text-gray-900 mb-3">{service.serviceName}</h4>
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div>
                          <div className="text-2xl font-bold text-indigo-600">{service.totalCards}</div>
                          <div className="text-xs text-gray-500">Total</div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-green-600">{service.completedCards}</div>
                          <div className="text-xs text-gray-500">Completed</div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-blue-600">{service.avgTime.toFixed(0)}</div>
                          <div className="text-xs text-gray-500">Avg Min</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default JobCardReportsPage;
